import { useEffect } from 'react';
import useIdentity from '../useIdentity';
import { useProductSettings, type ProductSettings } from '@workspace/utils-react';
import { AuthProvider } from '../../../credit-control/src/providers/AuthProvider';
import { LoadingScreen } from '../../../credit-control/src/components/LoadingScreen';
import type { ReactNode } from 'react';

interface AuthBridgeProviderProps {
  instanceId?: string;
  children: ReactNode;
}

export function AuthBridgeProvider({ instanceId, children }: AuthBridgeProviderProps): React.ReactElement {
  const { getAccessToken, signin, loading, error, isAuthenticated, user } = useIdentity();
  const settings = useProductSettings<ProductSettings>();

  useEffect(() => {
    if (!isAuthenticated && !loading && !error) {
      signin();
    }
  }, [isAuthenticated, loading, error, signin]);

  useEffect(() => {
    if (error && (error as any)?.message === 'login_required' && settings?.workspaceUrl) {
      const message = {
        type: 'RequestReauthentication',
      };
      window.parent.postMessage(JSON.stringify(message), settings.workspaceUrl);
    }
  }, [error, settings?.workspaceUrl]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Authentication Error: {error?.message}</h2>
      </div>
    );
  }

  if (isAuthenticated && user) {
    return (
      <AuthProvider getAccessToken={getAccessToken} instanceId={instanceId}>
        {children}
      </AuthProvider>
    );
  }

  return (
    <div className="unauthenticated-container">
      <p>Authentication required...</p>
    </div>
  );
}
